import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AudienceCarouselProps {
  children: Array<React.ReactNode>
  className?: string
}

const MOBILE_BREAKPOINT = 768
const MOBILE_CARD_FRACTION = 0.82
const MOBILE_GAP = 16

export function AudienceCarousel({ children, className }: AudienceCarouselProps) {
  const { t } = useTranslation('app')
  const [current, setCurrent] = useState(0)
  const [containerWidth, setContainerWidth] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)

  const count = children.length

  useEffect(() => {
    if (!containerRef.current) return
    const ro = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width)
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [])

  // Reset to first card when children change (e.g. section re-renders)
  useEffect(() => {
    setCurrent(0)
  }, [count])

  const isMobile = containerWidth > 0 && containerWidth < MOBILE_BREAKPOINT

  const canPrev = current > 0
  const canNext = current < count - 1

  const prev = useCallback(() => setCurrent((c) => Math.max(0, c - 1)), [])
  const next = useCallback(() => setCurrent((c) => Math.min(count - 1, c + 1)), [count])

  // Touch / swipe support
  const touchStartX = useRef<number | null>(null)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const delta = touchStartX.current - e.changedTouches[0].clientX
    if (Math.abs(delta) > 40) {
      if (delta > 0) next()
      else prev()
    }
    touchStartX.current = null
  }

  const cardWidth = containerWidth > 0 ? containerWidth * MOBILE_CARD_FRACTION : 0
  const peekOffset = containerWidth > 0 ? (containerWidth - cardWidth) / 2 : 0
  const translateX = containerWidth > 0 ? -(current * (cardWidth + MOBILE_GAP)) + peekOffset : 0

  // Desktop: render as a standard responsive grid
  if (!isMobile) {
    return (
      <div
        ref={containerRef}
        className={cn('grid grid-cols-2 lg:grid-cols-3 gap-4', className)}
      >
        {children}
      </div>
    )
  }

  // Mobile: render as a swipeable carousel
  return (
    <div className={cn('relative', className)}>
      <div
        ref={containerRef}
        className="overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          ref={trackRef}
          className="flex transition-transform duration-300 ease-out"
          style={{
            gap: `${MOBILE_GAP}px`,
            transform: `translateX(${translateX}px)`,
          }}
        >
          {children.map((child, i) => (
            <div
              key={i}
              className="shrink-0"
              style={{ width: `${cardWidth}px` }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Arrows */}
      {count > 1 && (
        <>
          <div
            className={cn(
              'absolute left-3 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-150',
              canPrev ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
          >
            <button
              type="button"
              onClick={prev}
              aria-label={t('audienceActions.prevAudience')}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-background text-foreground shadow-home-float transition-transform duration-200 hover:-translate-y-0.5"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </div>

          <div
            className={cn(
              'absolute right-3 top-1/2 z-20 translate-x-1/2 -translate-y-1/2 transition-opacity duration-150',
              canNext ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
          >
            <button
              type="button"
              onClick={next}
              aria-label={t('audienceActions.nextAudience')}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-background text-foreground shadow-home-float transition-transform duration-200 hover:-translate-y-0.5"
            >
              <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </div>
        </>
      )}

      {/* Dot indicators */}
      {count > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {children.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCurrent(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={cn(
                'rounded-full transition-all duration-300',
                i === current
                  ? 'w-4 h-1.5 bg-foreground'
                  : 'w-1.5 h-1.5 bg-foreground/25',
              )}
            />
          ))}
        </div>
      )}
    </div>
  )
}
